import { Box } from '@mui/material';
import { GuestJourneyActiveCards } from 'api/reports-service/guest-journey-reports.types';
import KpiCard from '../kpi-card/kpi-card.component';
import { useHideAllLocationSections } from './active-customers.hooks';

export type ActiveCustomersSummaryCardsProps = {
  data?: GuestJourneyActiveCards;
  loading?: boolean;
};

// Card order is an FE decision — the BE response is keyed by metric name.
// Titles, values, and any descriptions all arrive pre-formatted on the cards.
const CARD_ORDER: (keyof GuestJourneyActiveCards)[] = [
  'newMembers',
  'conversionRateToFirstOrder',
  'totalMembers',
  'membersWhoOrdered',
  'totalSlidersSaved',
  'totalChurnedSaved',
];

// New-member acquisition + first-order conversion are brand/all-locations
// metrics; they're only shown to an all-locations admin viewing all locations.
const ALL_LOCATIONS_ONLY_CARDS: (keyof GuestJourneyActiveCards)[] = [
  'newMembers',
  'conversionRateToFirstOrder',
];

const ActiveCustomersSummaryCards = ({
  data,
  loading = false,
}: ActiveCustomersSummaryCardsProps) => {
  // Hide the all-locations-only cards for a location-scoped admin, or whenever
  // the report is filtered to specific stores (the BE also nullifies these).
  const cardOrder = useHideAllLocationSections()
    ? CARD_ORDER.filter(id => !ALL_LOCATIONS_ONLY_CARDS.includes(id))
    : CARD_ORDER;

  return (
    <Box
      display="grid"
      gap={2}
      gridTemplateColumns={{
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(3, 1fr)',
        xl: 'repeat(6, 1fr)',
      }}
      data-testid="ActiveCustomers.SummaryCards"
    >
      {cardOrder.map(id => (
        <KpiCard
          key={id}
          card={data?.[id]}
          loading={loading}
          data-testid={`ActiveCustomers.SummaryCard.${id}`}
        />
      ))}
    </Box>
  );
};

export default ActiveCustomersSummaryCards;
